import { apiClient, apiClientPaginated } from '@/lib/api-client';

export interface InventoryTrackingSettings {
  track_batches: boolean;
  track_serials: boolean;
  /** True when no settings row exists and the vertical default applies. */
  is_default: boolean;
  business_type: string | null;
}
import type {
  Tenant,
  CreateTenantPayload,
  UpdateTenantPayload,
  TenantStatus,
  TenantLimits,
  TenantUsage,
  UpdateTenantLimitsPayload,
} from '@/types/tenant';

/**
 * Returned by `PATCH /tenants/:id/status` when the transition is critical and
 * was queued for a platform Admin to approve instead of being applied.
 */
export interface QueuedApproval {
  request_id: string;
  status: 'PENDING_APPROVAL';
}

/** True when the API queued the transition for approval rather than applying it. */
export function isQueuedApproval(result: Tenant | QueuedApproval): result is QueuedApproval {
  return typeof (result as QueuedApproval)?.request_id === 'string';
}

export const tenantService = {
  list(params: {
    page?: number;
    limit?: number;
    status?: string;
    business_type?: string;
    search?: string;
    /** Only businesses at or over 100 % of a limit this month. */
    over_limit?: boolean;
  }) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.status) query.set('status', params.status);
    if (params.business_type) query.set('business_type', params.business_type);
    if (params.search) query.set('search', params.search);
    if (params.over_limit) query.set('over_limit', 'true');
    const qs = query.toString();
    return apiClientPaginated<Tenant[]>(`/tenants${qs ? `?${qs}` : ''}`);
  },

  getById(id: string) {
    return apiClient<Tenant>(`/tenants/${id}`);
  },

  create(payload: CreateTenantPayload) {
    return apiClient<Tenant>('/tenants', { method: 'POST', body: payload });
  },

  update(id: string, payload: UpdateTenantPayload) {
    return apiClient<Tenant>(`/tenants/${id}`, { method: 'PATCH', body: payload });
  },

  /** Effective user/location ceilings, where each came from, and current usage. */
  getLimits(id: string) {
    return apiClient<TenantLimits>(`/tenants/${id}/limits`);
  },

  /** ADMIN+ only. Pass null for a field to clear the override back to the plan value. */
  updateLimits(id: string, payload: UpdateTenantLimitsPayload) {
    return apiClient<TenantLimits>(`/tenants/${id}/limits`, { method: 'PUT', body: payload });
  },

  /** This month's usage per metric against the effective limits. */
  getUsage(id: string) {
    return apiClient<TenantUsage>(`/tenants/${id}/usage`);
  },

  updateFeatureFlags(id: string, flags: string[]) {
    return apiClient<Tenant>(`/tenants/${id}/feature-flags`, {
      method: 'PATCH',
      body: { flags },
    });
  },

  /**
   * Critical lifecycle transitions (any `→ CANCELLED`) are NOT applied on the
   * spot — the API records an approval request and returns it with the tenant
   * untouched. Check the result with {@link isQueuedApproval} before telling
   * the user the status changed.
   */
  updateStatus(id: string, status: TenantStatus, reason?: string) {
    return apiClient<Tenant | QueuedApproval>(`/tenants/${id}/status`, {
      method: 'PATCH',
      body: reason ? { status, reason } : { status },
    });
  },

  provision(id: string) {
    return apiClient<Tenant>(`/tenants/${id}/provision`, { method: 'POST' });
  },

  softDelete(id: string) {
    return apiClient<{ id: string; status: string; cancelled_at: string }>(`/tenants/${id}`, { method: 'DELETE' });
  },

  updateProfile(
    id: string,
    payload: {
      business_name?: string;
      /** null clears a saved GSTIN. */
      gstin?: string | null;
      /** null clears a saved tax id. */
      tax_registration_number?: string | null;
      address?: string;
      city?: string;
      state?: string;
      pincode?: string;
      phone?: string;
      logo_url?: string;
      fssai_license?: string;
      /** India only; null = registered iff a GSTIN is set. */
      gst_registered?: boolean | null;
    },
  ) {
    return apiClient<Tenant>(`/tenants/${id}/profile`, {
      method: 'PATCH',
      body: payload,
    });
  },

  updateBackupSettings(id: string, backup_hour_utc: number) {
    return apiClient<Tenant>(`/tenants/${id}/backup-settings`, {
      method: 'PATCH',
      body: { backup_hour_utc },
    });
  },

  updateTaxSettings(
    id: string,
    payload: {
      default_tax_slab?: number;
      intra_state?: boolean;
      show_tax_inclusive?: boolean;
      default_hsn_code?: string;
      cash_rounding_step?: number;
      cash_rounding_mode?: 'HALF_UP' | 'UP' | 'DOWN';
    },
  ) {
    return apiClient<Tenant>(`/tenants/${id}/settings/tax`, {
      method: 'PATCH',
      body: payload,
    });
  },

  updateBranding(
    id: string,
    payload: {
      logo_url?: string;
      primary_color?: string;
      secondary_color?: string;
      receipt_footer?: string;
      invoice_header?: string;
      invoice_template?: string;
      show_logo_on_receipt?: boolean;
      show_gstin_on_receipt?: boolean;
    },
  ) {
    return apiClient<Tenant>(`/tenants/${id}/settings/branding`, {
      method: 'PATCH',
      body: payload,
    });
  },

  updateNotificationPrefs(
    userId: string,
    payload: Record<string, boolean>,
  ) {
    return apiClient<Record<string, boolean>>(`/users/${userId}/notification-preferences`, {
      method: 'PATCH',
      body: payload,
    });
  },

  getNotificationPrefs(userId: string) {
    return apiClient<Record<string, boolean>>(`/users/${userId}/notification-preferences`);
  },

  // ─── Return policy ────────────────────────────────────────────────────────
  // Tenant-configurable floor for who can issue credit notes / process
  // returns. Values: 10 = CASHIER+, 30 = MANAGER+, 50 = OWNER only.

  getReturnPolicy(tenantId: string) {
    return apiClient<{ min_role_level: number; approval_threshold: number }>(
      `/tenants/${tenantId}/settings/return-policy`,
    );
  },

  // ─── Inventory tracking ───────────────────────────────────────────────────
  // What Receive Goods captures per line (batch & expiry / serial numbers).

  getInventoryTracking(tenantId: string) {
    return apiClient<InventoryTrackingSettings>(`/tenants/${tenantId}/settings/inventory-tracking`);
  },

  setInventoryTracking(tenantId: string, settings: { track_batches: boolean; track_serials: boolean }) {
    return apiClient<InventoryTrackingSettings>(
      `/tenants/${tenantId}/settings/inventory-tracking`,
      { method: 'PATCH', body: settings },
    );
  },

  setReturnPolicy(
    tenantId: string,
    policy: { min_role_level?: 10 | 30 | 50; approval_threshold?: number },
  ) {
    return apiClient<{ min_role_level: number; approval_threshold: number }>(
      `/tenants/${tenantId}/settings/return-policy`,
      { method: 'PATCH', body: policy },
    );
  },
};
